/**
 * EnemyReveal — ⭐ the enemy reveal as a **Pokémon-style trading card** (BL-59).
 *
 * The owner's reframe (2026-06-06, supersedes the BL-48 "title-card"): present a
 * never-seen threat like a **trading card** — a scanned **THREAT DOSSIER** that
 * slams onto the left HUD. It is built on the reusable, rarity-tiered, gold-foil
 * `TradingCard` frame (the unified card-system language BL-60's skill Trainer
 * cards will reuse) with the threat **alive + dimensional** in a holo art window
 * (`EnemyHolo` — the REAL `EnemyShapes` silhouette, elaborated, NEVER raster),
 * its **codename + HP + abilities + weakness/resistance** mapped from the real
 * data, and a **rarity tier** (BASIC → gold-foil LEGENDARY) escalating to the
 * boss/final-boss apex.
 *
 * Beyond the single S86 card it adds the owner-specified **queue**: only ONE card
 * is visible at a time; new first-sightings enter a **FIFO queue** (slam-in →
 * readable hold → dismiss → a short gap → next). EPIC/LEGENDARY events (boss /
 * final / the Shield) **hold longer and jump the queue** (a boss is an event),
 * while trash waits FIFO; a **flood guard** caps the queue + accelerates the
 * holds so it is never blocking, never endless — mirroring the `BeatScheduler`
 * priority concept. The **Shield** mechanic gets its own reveal on its first
 * wave-15+ sighting (the hex-cage as the holo art).
 *
 * Determinism: render/DOM-only. The brief board freeze (EPIC+ events only)
 * reuses `GameLoop.hitStop` (loop-delay only — the sim witness bypasses it), so
 * `sim-fullrun-baseline` is byte-unchanged. The only randomness is the codename
 * decode + the holo motes (render-only, never a sim quantity).
 *
 * Coordination (no double-fire): keyed off the SAME first-encounter signal the
 * comms use — a new type on `enemy.spawned`, tracked in its own deduped `Set`
 * (cleared on the run's first wave). It fires the VISUAL reveal only; the
 * antagonist's comms line stays single-sourced in `CommsDirector`. Stealth types
 * are NOT pre-revealed (the cloak is the BL-49 Prunkstück) — they would be the
 * SECRET tier, but the stealth guard holds.
 */
import type { EventBus } from "../core/EventBus.ts";
import type { EnemyConfig } from "../entities/types.ts";
import { localizedName, t } from "../utils/i18n.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { COLORS } from "../core/constants.ts";
import { SHIELD_START_WAVE } from "../core/config.ts";
import { UI_DURATION } from "./uiTokens.ts";
import {
  TradingCard,
  __resetTradingCardForTest,
  type TradingCardContent,
  type TradingCardRarity,
} from "./TradingCard.ts";
import { EnemyHolo, type EnemyHoloOptions } from "./EnemyHolo.ts";
import {
  TIER_RANK,
  deriveEnemyRarity,
  buildEnemyCardContent,
  type EnemyRevealData,
} from "./enemyCardModel.ts";

// Re-export the card model's public surface — `EnemyReveal` was the home of the
// `EnemyRevealData` schema + the rarity ladder before the S159 extraction, so
// existing call sites (and tests) keep importing them from here unchanged.
export { deriveEnemyRarity, type EnemyRevealData };

const SLAM_MS = 460; // the slam entrance before the readable hold starts
const HOLD_BASE_MS = 5500; // owner-tuned readable hold (~5.5s base)
const HOLD_EVENT_MS = 7500; // EPIC/LEGENDARY (boss/final/shield) hold longer
const GAP_MS = 300; // the gap between a dismiss and the next card
const MAX_QUEUE = 4; // flood-guard cap (excludes the showing card)

/** One queued reveal — the built card content + (optional) holo options. */
interface RevealItem {
  /** Dedup / queue key (the enemy type name, or "__shield__"). */
  key: string;
  rarity: TradingCardRarity;
  content: TradingCardContent;
  holo: EnemyHoloOptions | null;
}

/** The live showing card + its holo, so dismiss can tear both down. */
interface ShowingCard {
  card: TradingCard;
  holo: EnemyHolo | null;
  item: RevealItem;
}

export class EnemyReveal {
  /** The overlay root — sits over the slim log when a reveal fires. */
  readonly el: HTMLDivElement;

  /** Optional board freeze (wired to `GameLoop.hitStop` in production). It
   *  self-guards reduced-motion at the GameLoop, so the reveal calls it blind. */
  private readonly boardHitStop: (() => void) | undefined;

  /** First-sighting de-dupe — the SAME signal the comms `seenEnemyTypes` uses. */
  private seen = new Set<string>();
  /**
   * Task-012 (AC-16) — the enemy types that actually GOT a reveal card this run
   * (i.e. `seen` minus the stealth-cloaked ones, which are added to `seen` on
   * spawn but deliberately never revealed, `onSpawned` below). This is the ONE
   * source of truth the in-game Enemy Library overlay reads for "discovered vs.
   * locked" — never a separate/duplicated tracking set, so the library and the
   * reveal card agree by construction. Run-scoped (cleared on `reset()`, same as
   * `seen`); there is no cross-run persisted bestiary (no `MetaState` field for
   * it) — a fresh run starts the library fully locked again, same as the reveal
   * queue itself.
   */
  private revealedTypes = new Set<string>();
  private enemies: Record<string, EnemyConfig> = {};
  private unsub: Array<() => void> = [];

  /** The FIFO queue (excludes the currently-showing card). */
  private queue: RevealItem[] = [];
  private showing: ShowingCard | null = null;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private gapTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  /** How many cards have been SHOWN (surfaced for the gate doc / tests). */
  revealCount = 0;

  constructor(parent: HTMLElement, opts: { boardHitStop?: () => void } = {}) {
    this.boardHitStop = opts.boardHitStop;
    this.el = document.createElement("div");
    this.el.className = "enemy-reveal";
    this.el.setAttribute("aria-hidden", "true");
    this.el.style.position = "absolute";
    // CR-4: the dossier card fills this overlay (.tc--enemy is 100%×100%), so on
    // iPhone it ran flush to the leading edge and the Dynamic Island clipped its
    // near side. Inset it off the leading edge + top on the phone tier so the card
    // clears the Island / rounded corner in landscape. D-335 — the nudge is now
    // DERIVED from the real per-side safe-area inset (`--inset-left-design` /
    // `--inset-top-design`, published by index.html's `fit()` script onto
    // `#game-container` — the SAME `inset ÷ live stage scale` division
    // SkillDeckBar.updateIslandClearance already uses for "// LOADOUT", D-333),
    // not a hard-coded design px. Custom properties inherit, so a plain
    // `max(<today's px>, var(--inset-*-design, 0px))` in the inline style
    // resolves correctly without this component owning a resize listener of
    // its own; a zero inset (desktop/iPad, or a rotation where the Island
    // sits on the OTHER edge) falls back to exactly today's 14px/26px, never
    // collapses to 0. iPad/desktop keep inset:0 (no Island), byte-identical.
    const phoneTier =
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("phone-tier");
    if (phoneTier) {
      this.el.style.top = "max(14px, var(--inset-top-design, 0px))";
      this.el.style.left = "max(26px, var(--inset-left-design, 0px))";
      this.el.style.right = "0";
      this.el.style.bottom = "0";
    } else {
      this.el.style.inset = "0";
    }
    this.el.style.zIndex = "5";
    this.el.style.display = "none";
    this.el.style.pointerEvents = "none";
    parent.appendChild(this.el);
  }

  /** How many cards are waiting in the queue (excludes the showing card). */
  get queuedCount(): number {
    return this.queue.length;
  }

  /** The rarity of the card on screen now, or null when idle. */
  get currentRarity(): TradingCardRarity | null {
    return this.showing?.item.rarity ?? null;
  }

  /** The keys (type names) currently queued, in show order. */
  peekQueue(): string[] {
    return this.queue.map((q) => q.key);
  }

  /**
   * Wire the first-sighting hook: a new (non-stealth) enemy *type* on
   * `enemy.spawned` enqueues its card; the first wave-15+ shield enqueues the
   * Shield card. The seen-set clears on the run's first wave (a 2nd run
   * re-reveals) — mirroring `CommsDirector.seenEnemyTypes`.
   */
  attach(bus: EventBus, enemies: Record<string, EnemyConfig>): void {
    this.detach();
    this.enemies = enemies;
    this.unsub.push(
      bus.on("enemy.spawned", (d: unknown) => {
        const enemy = (d as { enemy?: RevealEnemyLike } | undefined)?.enemy;
        this.onSpawned(enemy);
      }),
      bus.on("wave.shielded.incoming", () => this.revealShield()),
      bus.on("wave.started", (d: unknown) => {
        const wave = (d as { wave?: number } | undefined)?.wave;
        if (wave === 1) this.reset();
      }),
    );
  }

  detach(): void {
    for (const off of this.unsub) off();
    this.unsub = [];
  }

  private onSpawned(enemy: RevealEnemyLike | undefined): void {
    const type = enemy?.type;
    if (!type) return;
    if (this.seen.has(type)) return;
    this.seen.add(type);
    // Stealth guard — never pre-reveal a cloaked type's existence.
    if (enemy?.stealth) return;
    this.revealedTypes.add(type);
    this.reveal(this.toRevealData(enemy));
  }

  /** Task-012 (AC-16) — has this enemy *type* (the config's display `name`, the
   *  same key `seen`/`revealedTypes` use) been revealed this run? The Enemy
   *  Library overlay's discovery gate reads this directly — no separate state. */
  isDiscovered(typeName: string): boolean {
    return this.revealedTypes.has(typeName);
  }

  /** Build the render projection: prefer the canonical type config (it carries
   *  `shortName`/`special`/matchups); fall back to the live enemy's own fields.
   *  Display name/code go through the i18n seam (`enemy.<id>` / `.short`) with
   *  the JSON value as fallback — the sim-side `type` string stays untouched. */
  private toRevealData(enemy: RevealEnemyLike): EnemyRevealData {
    const entry = Object.entries(this.enemies).find(
      ([, c]) => c.name === enemy.type,
    );
    const [id, cfg] = entry ?? [undefined, undefined];
    const rawName = enemy.type ?? "UNKNOWN";
    return {
      name: id ? localizedName("enemy", id, rawName) : rawName,
      shortName:
        id && cfg?.shortName
          ? localizedName("enemy", `${id}.short`, cfg.shortName)
          : cfg?.shortName,
      hp: cfg?.hp ?? enemy.maxHp ?? enemy.hp ?? 0,
      speed: Math.round(cfg?.speed ?? enemy.speed ?? 0),
      reward: cfg?.reward ?? enemy.reward ?? 0,
      shape: cfg?.shape ?? enemy.shape ?? "diamond",
      colour: enemy.color ?? cfg?.color ?? COLORS.ORACLE_RED,
      special: cfg?.special ?? null,
      livesDamage: cfg?.livesDamage,
      firewallDamage: cfg?.firewallDamage,
      weakness: cfg?.weakness ?? null,
      resistance: cfg?.resistance ?? null,
      immunities: cfg?.immunities,
      stealth: enemy.stealth,
    };
  }

  /**
   * Enqueue a reveal card for one enemy type. Public + reusable (the gate doc
   * and tests call it straight). Stealth types are skipped (the cloak guard).
   * Non-blocking: gameplay continues, the card auto-dismisses + the queue drains.
   */
  reveal(data: EnemyRevealData): void {
    if (this.destroyed || data.stealth) return;
    this.enqueue(this.buildEnemyItem(data));
  }

  /**
   * Enqueue the Shield reveal — the first time the wave-15+ shield mechanic
   * appears. Deduped (once per run via the seen-set); an EPIC event (jumps the
   * queue, holds longer). Public for the gate doc.
   */
  revealShield(): void {
    if (this.destroyed) return;
    if (this.seen.has("__shield__")) return;
    this.seen.add("__shield__");
    this.enqueue(this.buildShieldItem());
  }

  // ─── The queue ───────────────────────────────────────────────────────────────

  /** Insert with tier priority (EPIC+ jump ahead of trash) + a flood guard, then
   *  show immediately if nothing is on screen. */
  private enqueue(item: RevealItem): void {
    const rank = TIER_RANK[item.rarity];
    if (rank >= 2) {
      // An event jumps ahead of all lower-rank cards (after queued events).
      let i = 0;
      while (i < this.queue.length && TIER_RANK[this.queue[i]!.rarity] >= 2)
        i++;
      this.queue.splice(i, 0, item);
    } else {
      this.queue.push(item); // FIFO among trash/elite
    }

    // Flood guard — drop the newest lowest-rank waiter so it never backs up.
    while (this.queue.length > MAX_QUEUE) {
      let dropIdx = this.queue.length - 1;
      let dropRank = Infinity;
      for (let k = this.queue.length - 1; k >= 0; k--) {
        const r = TIER_RANK[this.queue[k]!.rarity];
        if (r < dropRank) {
          dropRank = r;
          dropIdx = k;
        }
      }
      this.queue.splice(dropIdx, 1);
    }

    if (!this.showing) this.advance();
  }

  /** Show the next queued card (no-op while one is already on screen). */
  private advance(): void {
    if (this.showing || this.destroyed) return;
    const item = this.queue.shift();
    if (!item) {
      this.el.style.display = "none";
      return;
    }
    this.show(item);
  }

  /** Mount + animate one card; arm its readable-hold dismiss timer. */
  private show(item: RevealItem): void {
    this.revealCount++;
    this.el.style.display = "flex";

    const card = new TradingCard(item.content);
    let holo: EnemyHolo | null = null;
    this.el.appendChild(card.el);
    if (item.holo) {
      holo = new EnemyHolo(item.holo);
      card.mountHolo(holo.el);
    }
    this.showing = { card, holo, item };

    card.playReveal();
    void card.el.offsetWidth; // force layout so the holo has a sized host
    holo?.start();

    // The board freeze coincides with an EVENT card's slam (loop-delay only).
    if (TIER_RANK[item.rarity] >= 2) this.boardHitStop?.();

    const reduced = isReducedMotion();
    const hold = this.holdFor(item);
    this.clearHold();
    this.holdTimer = setTimeout(
      () => this.dismissCurrent(),
      (reduced ? 0 : SLAM_MS) + hold,
    );
  }

  /** The readable hold for an item — EPIC+ events linger; the hold accelerates
   *  under queue pressure (the flood-guard's "never endless" half). */
  private holdFor(item: RevealItem): number {
    const base = TIER_RANK[item.rarity] >= 2 ? HOLD_EVENT_MS : HOLD_BASE_MS;
    const pressure = Math.min(this.queue.length, MAX_QUEUE);
    const scale = 1 - 0.12 * pressure; // up to ~48% faster when fully backed up
    return Math.max(2200, Math.round(base * scale));
  }

  /** Dismiss the on-screen card, then (after the fade) drain the next. */
  private dismissCurrent(): void {
    const cur = this.showing;
    if (!cur) return;
    this.clearHold();
    cur.card.dismiss();
    const finalize = (): void => {
      cur.holo?.destroy();
      cur.card.destroy();
      if (this.showing === cur) this.showing = null;
      this.afterDismiss();
    };
    if (isReducedMotion()) finalize();
    else this.hideTimer = setTimeout(finalize, UI_DURATION.settle + 40);
  }

  private afterDismiss(): void {
    if (this.destroyed) return;
    if (this.queue.length === 0) {
      this.el.style.display = "none";
      return;
    }
    this.gapTimer = setTimeout(() => this.advance(), GAP_MS);
  }

  // ─── Item builders (data → card) ─────────────────────────────────────────────

  private buildEnemyItem(d: EnemyRevealData): RevealItem {
    // The data→card projection is the shared `enemyCardModel` (the S159 lift) —
    // the in-run reveal and the Enemy Design Lab build the identical card.
    const { rarity, content, holo } = buildEnemyCardContent(d);
    return { key: d.name, rarity, content, holo };
  }

  private buildShieldItem(): RevealItem {
    const colour = COLORS.ICE_CYAN; // central — the ice/azure shield read
    // The player-facing prose + word-labels resolve at render through the i18n
    // seam (the S162 dossier pattern): the EN value is BOTH the table entry AND
    // the `t()` fallback, so the EN card stays byte-identical and DE lights up.
    // CLASS-S / SENTINEL-7 / the ◈/⚠/· symbols are code tokens kept verbatim
    // across both locales. The {{wave}} (SHIELD_START_WAVE) stays data-driven —
    // interpolated, never hardcoded — in EN and DE alike (D-256).
    const content: TradingCardContent = {
      kind: "enemy",
      rarity: "epic",
      colour,
      classification: t("shield.classification", undefined, "⚠ DEFENSE PROTOCOL"),
      classBadge: "CLASS-S",
      name: t("shield.name", undefined, "SHIELD"),
      subtitle: t("shield.subtitle", undefined, "REINFORCED · HEX-CAGE"),
      stats: [
        {
          k: t("shield.stat.protocol.k", undefined, "PROTOCOL"),
          v: t("shield.stat.protocol.v", undefined, "REINFORCED"),
        },
        {
          k: t("shield.stat.online.k", undefined, "ONLINE"),
          v: t(
            "shield.stat.online.v",
            { wave: SHIELD_START_WAVE },
            `WAVE ${SHIELD_START_WAVE}+`,
          ),
        },
      ],
      ability: {
        title: t("shield.ability.title", undefined, "◈ HEX-CAGE"),
        desc: t(
          "shield.ability.desc",
          undefined,
          "A faceted shield absorbs damage before the threat's core is touched. Break the cage with sustained fire, then finish the kill.",
        ),
      },
      footerLeft: t("shield.footerLeft", undefined, "SENTINEL-7 · SHIELD"),
      flavour: t("shield.flavour", undefined, "Some of them came back armoured."),
      pips: 3,
    };
    return {
      key: "__shield__",
      rarity: "epic",
      content,
      holo: { shape: "block", colour, tier: "epic", subject: "shield" },
    };
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  private clearHold(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  private clearAllTimers(): void {
    this.clearHold();
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.gapTimer) {
      clearTimeout(this.gapTimer);
      this.gapTimer = null;
    }
  }

  /** Clear the run-scoped seen-set + the queue + tear down any in-flight card. */
  reset(): void {
    this.seen.clear();
    this.revealedTypes.clear();
    this.clearAllTimers();
    this.queue = [];
    if (this.showing) {
      this.showing.holo?.destroy();
      this.showing.card.destroy();
      this.showing = null;
    }
    this.el.style.display = "none";
  }

  destroy(): void {
    this.destroyed = true;
    this.detach();
    this.reset();
    this.el.remove();
  }
}

/** A structural view of the live `Enemy` — only the render-only fields we read. */
interface RevealEnemyLike {
  type?: string;
  color?: string;
  shape?: string;
  hp?: number;
  maxHp?: number;
  speed?: number;
  reward?: number;
  stealth?: boolean;
}

/** Test/reset hook — drops the injected `TradingCard` style guard. */
export function __resetEnemyRevealForTest(): void {
  __resetTradingCardForTest();
}
